// src/modules/app-views/fiscal-config/pages/Document/tabs/CertificateTab/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Grid } from 'antd';
import { ExclamationCircleOutlined, DeleteOutlined } from '@ant-design/icons';
import utils from 'utils/utils';
import axiosService from 'services/axiosService';
import { API } from 'constants/Api';
import translateX from 'utils/translateX';
import SimpleLoadingArea from 'components/SimpleComponents/SimpleLoadingArea';
import { useDocumentContext } from 'components/core/DocumentProvider';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import { useState, useCallback, useEffect } from 'react';
import { openModal } from 'components/core/GenericModal';
import CertificateUploadModal from 'modules/app-views/account/pages/Subscription/modals/CertificateUploadModal';
import useZustand from 'hooks/useZustand';
import authService from 'services/authService';
import appService from 'services/appService';
import fiscalService from 'services/fiscalService';

// Card Components
import CertificateCard from './CertificateCard';
import FiscalIdCard from './FiscalIdCard';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const { useBreakpoint } = Grid;

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const CertificateTab = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { data, loading } = useDocumentContext();
  const { form, locked } = useSimpleFormContext();
  const storedSubscription = useZustand('auth', 'subscription');
  const apiGlobal = useZustand('global', 'useApi');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isMobile = !utils.getBreakPoint(useBreakpoint()).includes('sm');
  // Certificate data comes from aggregate in data.fiscal.certificate
  const certificate = data?.fiscal?.certificate || null;
  // Get UF (state) for SEFAZ links
  const entityUf = data?.address?.region || null;
  const ieLinkUrl = fiscalService.getIeLinks(entityUf, 'url');
  const ieLinkName = fiscalService.getIeLinks(entityUf, 'name');
  // Check if fiscal engines are configured
  const hasEngines = data?.fiscal?.providers?.length > 0;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [localCertificate, setLocalCertificate] = useState(null);
  const [nfceEnabled, setNfceEnabled] = useState(data?.fiscalData?.nfce?.isEnabled || false);
  const [nfeEnabled, setNfeEnabled] = useState(data?.fiscalData?.nfe?.isEnabled || false);
  const [nfseEnabled, setNfseEnabled] = useState(data?.fiscalData?.nfse?.isEnabled || false);

  // Use local certificate if available (just uploaded), otherwise use server data
  const displayCertificate = localCertificate || certificate;

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Sync enabled states when data changes
  useEffect(() => {
    if (data?.fiscalData?.nfce?.isEnabled !== undefined) {
      setNfceEnabled(data.fiscalData.nfce.isEnabled);
    }
    if (data?.fiscalData?.nfe?.isEnabled !== undefined) {
      setNfeEnabled(data.fiscalData.nfe.isEnabled);
    }
    if (data?.fiscalData?.nfse?.isEnabled !== undefined) {
      setNfseEnabled(data.fiscalData.nfse.isEnabled);
    }
  }, [
    data?.fiscalData?.nfce?.isEnabled,
    data?.fiscalData?.nfe?.isEnabled,
    data?.fiscalData?.nfse?.isEnabled,
  ]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleCertificateSuccess = useCallback(
    async ({ certificateData, certificateFile, stateRegistrationData }) => {
      // Update local state to show certificate immediately
      setLocalCertificate(certificateData);

      // Update parent form with certificate data
      if (form) {
        const fieldsToUpdate = {
          'fiscalData.certificate': certificateData,
          _certificateFile: certificateFile,
        };

        // If state registration was retrieved from CCC/SEFAZ, update the form field
        if (stateRegistrationData?.stateRegistration) {
          fieldsToUpdate['fiscalData.stateRegistration'] =
            stateRegistrationData.stateRegistration;

          // Also update taxRegime if available and not already set
          const currentTaxRegime = form.getFieldValue(['fiscalData', 'taxRegime']);
          if (!currentTaxRegime && stateRegistrationData.taxRegime) {
            fieldsToUpdate['fiscalData.taxRegime'] = stateRegistrationData.taxRegime;
          }
        }

        form.setFieldsValue(fieldsToUpdate);
      }

      // Refresh page data to get updated certificate info from backend
      if (apiGlobal?.refresh) {
        await apiGlobal.refresh();
      }

      // If this entity is the main company of the subscription, refresh auth state
      const entityId = data?.entityId || data?._id;
      if (entityId && storedSubscription?.entityId === entityId) {
        await authService.signInRefresh();
      }
    },
    [form, apiGlobal, data, storedSubscription]
  );

  const handleOpenCertificateModal = useCallback(() => {
    const entityId = data?.entityId || data?._id;
    const companyCnpj = data?.cnpj?.value || data?.cnpj;
    openModal(CertificateUploadModal, {
      entityId,
      companyCnpj,
      onSuccess: handleCertificateSuccess,
    });
  }, [data, handleCertificateSuccess]);

  const handleDeleteCertificate = useCallback(() => {
    // Rule 1: Do not allow removing certificate if any fiscal service is enabled
    const hasEnabledServices = nfceEnabled || nfeEnabled || nfseEnabled;
    if (hasEnabledServices) {
      appService.notification({
        flag: 'warning',
        title: 'cannot_remove_certificate_with_services',
        description: 'cannot_remove_certificate_with_services_description',
        duration: 6,
      });
      return;
    }

    const handleConfirm = async () => {
      const entityId = data?.entityId || data?._id;
      if (!entityId) return;

      const loadingMsg = await appService.message({
        flag: 'loading',
        text: 'removing_certificate',
        duration: 0,
      });

      try {
        const [response] = await axiosService.delete({
          url: API.FISCAL.CERTIFICATE_DELETE.replace(':entityId', entityId),
          returnAsObject: true,
        });

        if (!response || !response.success) {
          throw new Error('Delete failed');
        }

        // Clear local certificate state
        setLocalCertificate(null);

        // Refresh page data
        if (apiGlobal?.refresh) {
          await apiGlobal.refresh();
        }

        // If main company, refresh auth
        if (entityId && storedSubscription?.entityId === entityId) {
          await authService.signInRefresh();
        }

        loadingMsg.update({
          flag: 's',
          text: 'certificate_removed_success',
          duration: 2,
        });
      } catch (error) {
        console.error('[CertificateTab] Delete certificate error:', error);
        loadingMsg.update({
          flag: 'e',
          text: 'certificate_removed_error',
          duration: 3,
        });
      }
    };

    appService.modal({
      flag: 'confirm',
      title: translateX('remove_certificate_confirm_title'),
      icon: <ExclamationCircleOutlined />,
      content: <p>{translateX('remove_certificate_confirm_description')}</p>,
      okText: 'yes',
      cancelText: 'no',
      onOk: handleConfirm,
      okButtonProps: { danger: true, icon: <DeleteOutlined /> },
      centered: true,
      maskClosable: true,
    });
  }, [data, apiGlobal, storedSubscription, nfceEnabled, nfeEnabled, nfseEnabled]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="container" style={{ position: 'relative', minHeight: 120 }}>
      <SimpleLoadingArea name="fiscalData" isFetching={loading} data={data} />

      {/* DIGITAL CERTIFICATE CARD */}
      <CertificateCard
        displayCertificate={displayCertificate}
        isMobile={isMobile}
        locked={locked}
        onUpload={handleOpenCertificateModal}
        onDelete={handleDeleteCertificate}
      />

      {/* FISCAL IDENTIFICATION CARD */}
      {displayCertificate ? (
        <FiscalIdCard
          displayCertificate={displayCertificate}
          nfceEnabled={nfceEnabled}
          nfeEnabled={nfeEnabled}
          nfseEnabled={nfseEnabled}
          ieLinkUrl={ieLinkUrl}
          ieLinkName={ieLinkName}
        />
      ) : null}
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default CertificateTab;
